import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell, { useShellUser } from '../components/AppShell';
import { ReactComponent as DateIcon } from '../assets/icons/icon-date-fill.svg';
import { ReactComponent as GiftIcon } from '../assets/icons/icon-gift-outline.svg';
import { ReactComponent as LocationIcon } from '../assets/icons/icon-location.svg';
import { ReactComponent as MedalIcon } from '../assets/icons/icon-medal.svg';
import { ReactComponent as RankMedalIcon } from '../assets/icons/icon-medal-rank.svg';
import { ReactComponent as UnlockIcon } from '../assets/icons/icon-unlock.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as TicketIcon } from '../assets/icons/ticket-fill.svg';
import { ReactComponent as PersonIcon } from '../assets/icons/person.svg';
import '../styles/RewardDetail.css';

const COLLAPSED_ROWS = 8;
const MAX_ROWS = 100;
const DEFAULT_PERIOD_END_TIME = '16:00:00';
const DEFAULT_HOW_TO_WIN =
  'Shop at your usual stores using your linked card to earn points automatically. ' +
  'Climb the leaderboard and finish in first place before the competition ends to win.';

function formatNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
}

function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('s3://app.gethomecrowd.com/')) {
    return url.replace('s3://app.gethomecrowd.com/', 'https://app.gethomecrowd.com/');
  }
  if (url.startsWith('s3://')) return url.replace('s3://', 'https://');
  return `https://app.gethomecrowd.com/${url.replace(/^\/+/, '')}`;
}

function errorText(error, fallback) {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    for (const key of ['error', 'detail', 'message', 'contactValue']) {
      const value = data[key];
      if (typeof value === 'string' && value.trim()) return value;
      if (Array.isArray(value) && value[0]) return String(value[0]);
    }
  }
  return error?.message || fallback;
}

function parseRewardDate(value) {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDateEndOfDay(value) {
  const date = parseRewardDate(value);
  if (!date) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) date.setHours(23, 59, 59, 999);
  return date;
}

function rewardStartDate(reward) {
  const blocks = [reward?.raffle_info, reward?.auction_info].filter(Boolean);
  for (let i = 0; i < blocks.length; i += 1) {
    const found = blocks[i].start_date || blocks[i].startDate;
    if (found) return found;
  }
  return reward?.start_date || null;
}

function isRewardBeforeStart(reward, now) {
  const start = parseRewardDate(rewardStartDate(reward));
  return !!(start && now < start.getTime());
}

function normalizeTime(value) {
  if (!value) return DEFAULT_PERIOD_END_TIME;
  const raw = String(value);
  if (/^\d{2}:\d{2}/.test(raw)) return raw.length >= 8 ? raw.slice(0, 8) : `${raw.slice(0, 5)}:00`;
  return DEFAULT_PERIOD_END_TIME;
}

function parsePeriodEndTimestamp(raw, fallbackDateOnly = null, fallbackEndTime = null) {
  const endTime = normalizeTime(fallbackEndTime);
  if (raw) {
    const str = String(raw);
    if (str.includes('T')) {
      const ms = new Date(str).getTime();
      if (Number.isFinite(ms)) return ms;
    }
    const ms = new Date(`${str.split('T')[0]}T${endTime}`).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  if (fallbackDateOnly) {
    const ms = new Date(`${fallbackDateOnly}T${endTime}`).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

function splitDelta(delta) {
  return {
    days: Math.floor(delta / 86400000),
    hours: Math.floor((delta % 86400000) / 3600000),
    minutes: Math.floor((delta % 3600000) / 60000),
    seconds: Math.floor((delta % 60000) / 1000),
  };
}

function compactCountdown(end, now, endedText = 'Ended') {
  if (!end || !Number.isFinite(end)) return '';
  const delta = end - now;
  if (delta <= 0) return endedText;
  const { days, hours, minutes, seconds } = splitDelta(delta);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  if (minutes > 0) return `${minutes}m ${seconds}s left`;
  return `${seconds}s left`;
}

function longCountdown(end, now) {
  const delta = end - now;
  if (delta <= 0) return '';
  const { days, hours, minutes, seconds } = splitDelta(delta);
  if (days > 0) return `Ends in: ${days}d ${hours}h ${minutes}m ${seconds}s`;
  if (hours > 0) return `Ends in: ${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `Ends in: ${minutes}m ${seconds}s`;
  return `Ends in: ${seconds}s`;
}

function formatLongDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatOpensDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  return `${date.toLocaleDateString('en-US', { month: 'long' })} ${day}${suffix}`;
}

function formatEventDateLabel(raw) {
  if (!raw) return '';
  const value = String(raw);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hasTime = /T\d{2}:\d{2}/.test(value) || /\d{1,2}:\d{2}/.test(value);
  const datePart = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  if (!hasTime || /^\d{4}-\d{2}-\d{2}$/.test(value)) return datePart;
  return `${datePart} at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

function formatRewardTypeLabel(value) {
  if (!value) return 'Merchandise';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function sameId(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function isEarlyReleaseUser(user) {
  return user?.active_school?.early_release === true;
}

function cardLinkStatus(user, cardsResponse) {
  if (isEarlyReleaseUser(user)) return 'linked';
  if (!cardsResponse) return 'unknown';
  const raw = cardsResponse?.results || cardsResponse?.data || cardsResponse;
  if (!Array.isArray(raw)) return 'unknown';
  return raw.some((card) => (card?.status || 'active') === 'active') ? 'linked' : 'unlinked';
}

function productImages(product) {
  if (Array.isArray(product?.images) && product.images.length) {
    return product.images
      .slice()
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((img) => normalizeMediaUrl(typeof img === 'string' ? img : img?.image_path))
      .filter(Boolean);
  }
  return product?.image_url ? [product.image_url] : [];
}

function InfoCard({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="rd-info">
      {rows.map(({ key, Icon, title, body }) => (
        <div key={key} className="rd-info-row">
          <span className="rd-info-icon" aria-hidden="true">
            <Icon />
          </span>
          <span className="rd-info-copy">
            <strong>{title}</strong>
            <span>{body}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function Gallery({ images, overlay, dimmed }) {
  const [active, setActive] = useState(0);
  const slides = images.length ? images : [null];
  const current = slides[Math.min(active, slides.length - 1)];

  useEffect(() => {
    setActive(0);
  }, [images.length]);

  return (
    <div className={`rd-gallery${dimmed ? ' is-dimmed' : ''}`}>
      <div className="rd-stage">
        {current ? <img src={current} alt="" /> : <span className="rd-stage-ph" />}
        {overlay ? <span className="rd-overlay">{overlay}</span> : null}
      </div>
      {images.length > 1 ? (
        <div className="rd-thumbs" style={{ gridTemplateColumns: `repeat(${Math.min(images.length, 4)}, minmax(0, 1fr))` }}>
          {images.map((url, index) => (
            <button
              key={url}
              type="button"
              className={index === active ? 'is-on' : ''}
              onClick={() => setActive(index)}
              aria-label={`Show image ${index + 1}`}
            >
              <img src={url} alt="" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PointsOverlay({ points }) {
  return (
    <>
      <b>{formatNumber(points)}</b>
      <small>pts</small>
    </>
  );
}

function DetailFrame({ children }) {
  return (
    <div className="rd-page">
      <Link to="/rewards" className="rd-back">
        <svg viewBox="0 0 8 14" aria-hidden="true">
          <path d="M6.5 1.5 1.5 7l5 5.5" />
        </svg>
        Rewards
      </Link>
      {children}
    </div>
  );
}

function Badge({ children, icon }) {
  return (
    <span className="rd-badge">
      {icon}
      {children}
    </span>
  );
}

function Dialog({ label, onClose, children, busy }) {
  return (
    <div className="rd-modal" role="presentation" onClick={busy ? undefined : onClose}>
      <div className="rd-dialog" role="dialog" aria-modal="true" aria-label={label} onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function LinkCardBanner() {
  return (
    <div className="rd-linkcard">
      <span className="rd-linkcard-copy">
        <strong>Link a card to unlock rewards</strong>
        <span>You can browse, but you won&apos;t be able to redeem rewards until a card is linked.</span>
      </span>
      <Link to="/settings?tab=cards" className="rd-btn is-primary">
        Link card
      </Link>
    </div>
  );
}

function ThanksView({ product, result, onClose }) {
  const { mode, pointsSpent, entries, bidAmount } = result;
  const isRaffle = product.redemption_type === 'raffle';
  const title =
    mode === 'bid' ? 'Bid Placed Successfully!' : isRaffle ? 'Raffle Entry Successful!' : 'Redemption Successful!';
  const message =
    mode === 'bid'
      ? 'Your bid has been placed successfully! You can view the current auction status and place additional bids if needed.'
      : isRaffle
        ? entries > 1
          ? `Thank you for entering the raffle! Your ${entries} entries have been recorded and you will be notified if you win.`
          : 'Thank you for entering the raffle! Your entry has been recorded and you will be notified if you win.'
        : 'Thank you for your redemption. Your reward is being processed.';
  const spent =
    mode === 'bid'
      ? `Bid Amount: ${formatNumber(bidAmount)} pts`
      : isRaffle && entries > 1
        ? `Entries: ${entries} (${formatNumber(pointsSpent)} pts)`
        : `Points Spent: ${formatNumber(pointsSpent)} pts`;
  const info =
    mode === 'bid'
      ? 'Your bid is now active and will compete until the auction ends. You can place additional bids to stay competitive.'
      : isRaffle
        ? 'You will receive a confirmation email with your entry details. The drawing will take place on the scheduled date.'
        : 'You will receive a confirmation email with details about your redemption.';
  const channel = product.fulfillment_channel;
  const image = productImages(product)[0];

  return (
    <div className="rd-thanks">
      <span className="rd-thanks-mark" aria-hidden="true">✓</span>
      <h3>{title}</h3>
      <p>{message}</p>
      <div className="rd-thanks-item">
        {image ? <img src={image} alt="" /> : <span className="rd-stage-ph" />}
        <span>
          <strong>{product.title}</strong>
          <span>{spent}</span>
        </span>
      </div>
      <p className="rd-muted">{info}</p>
      {channel ? (
        <p className="rd-muted">
          Fulfillment:{' '}
          {channel === 'shipping'
            ? 'Shipping to your address'
            : channel === 'email_code'
              ? 'Email with code'
              : channel === 'in_store'
                ? 'Pick up in store'
                : 'In app'}
        </p>
      ) : null}
      <div className="rd-dialog-actions">
        <button type="button" className="rd-btn is-primary" onClick={onClose}>
          {mode === 'bid' ? 'Back to auction' : 'Done'}
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ product, mode, bidAmount, availablePoints, onClose, onDone }) {
  const [quantity, setQuantity] = useState(1);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageFlash, setAgeFlash] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const isRaffle = product.redemption_type === 'raffle';
  const isBid = mode === 'bid';
  const isStripe = mode === 'stripe';
  const pointsCost = Number(product.points_cost) || 0;
  const stripeCents = Number(product.cash_price_cents);
  const drawingPassed =
    isRaffle && !!product.raffle_info?.drawing_date && new Date() >= new Date(product.raffle_info.drawing_date);
  const currentUserBid = Number(product.auction_info?.user_current_bid) || 0;

  const maxEntries = useMemo(() => {
    if (!isRaffle || isStripe) return 1;
    const info = product.raffle_info || {};
    const limits = [];
    if (pointsCost > 0) limits.push(Math.floor(availablePoints / pointsCost));
    if (info.max_entries_per_user) limits.push(info.max_entries_per_user - (info.user_entries || 0));
    if (info.max_total_entries) limits.push(info.max_total_entries - (info.total_entries || 0));
    if (!limits.length) return 1;
    return Math.max(0, Math.min(...limits));
  }, [isRaffle, isStripe, product.raffle_info, pointsCost, availablePoints]);

  const totalCost = isRaffle ? pointsCost * quantity : pointsCost;
  const insufficient = !isStripe && !isBid && availablePoints < totalCost;
  const requiresAge = isRaffle && !isBid;

  const confirm = async () => {
    if (requiresAge && !ageConfirmed) {
      setAgeFlash(true);
      setTimeout(() => setAgeFlash(false), 1200);
      return;
    }
    if (!isBid && isRaffle && drawingPassed) {
      setError('This raffle has ended and is no longer accepting entries.');
      return;
    }
    if (insufficient) {
      setError(`You need ${formatNumber(totalCost)} points but only have ${formatNumber(availablePoints)} available.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (isStripe) {
        const data = await apiService.createStripeRewardCheckoutSession(product.id);
        if (!data?.url) {
          setError('Could not open checkout.');
          setBusy(false);
          return;
        }
        window.location.assign(data.url);
        return;
      }
      if (isBid) {
        const latest = await apiService.getReward(product.id).catch(() => null);
        const auction = latest?.auction_info;
        if (auction) {
          const ended =
            ['completed', 'expired', 'ended'].includes(auction.status) ||
            (auction.end_date && new Date() >= new Date(auction.end_date));
          if (ended) {
            setError('This auction has ended and is no longer accepting bids.');
            setBusy(false);
            return;
          }
          const highest = Number(auction.current_highest_bid) || 0;
          if (highest && highest >= bidAmount) {
            setError(
              `Your bid of ${formatNumber(bidAmount)} pts is no longer sufficient. The current highest bid is ${formatNumber(highest)} pts. Please go back and place a higher bid.`
            );
            setBusy(false);
            return;
          }
        }
        await apiService.placeBid(product.auction_info.id, bidAmount);
        setResult({ mode: 'bid', bidAmount });
      } else if (isRaffle) {
        for (let i = 0; i < quantity; i += 1) {
          await apiService.createRedemption(product.id, pointsCost, { ageConfirmed });
        }
        setResult({ mode: 'points', pointsSpent: totalCost, entries: quantity });
      } else {
        await apiService.createRedemption(product.id, pointsCost);
        setResult({ mode: 'points', pointsSpent: pointsCost, entries: 0 });
      }
      onDone();
    } catch (err) {
      setError(errorText(err, isBid ? 'Failed to place bid. Please try again.' : 'Failed to redeem item. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Dialog label="Confirmation" onClose={onClose}>
        <ThanksView product={product} result={result} onClose={onClose} />
      </Dialog>
    );
  }

  const costLabel = isStripe ? 'Price (USD):' : isBid ? 'Bid Amount:' : 'Cost:';
  const costValue = isStripe
    ? `$${(stripeCents / 100).toFixed(2)}`
    : isBid
      ? `${formatNumber(bidAmount)} pts`
      : `${formatNumber(totalCost)} pts`;
  const balanceLabel = isStripe ? 'Your points (unchanged)' : 'Your points';
  const balanceValue = isBid
    ? `${formatNumber(availablePoints + currentUserBid)} pts`
    : `${formatNumber(availablePoints)} pts`;
  const remainingValue = isBid
    ? `${formatNumber(availablePoints + currentUserBid - bidAmount)} pts`
    : `${formatNumber(Math.max(0, availablePoints - totalCost))} pts`;
  const note = isStripe
    ? 'You will pay with a card on the next screen (Stripe). Points will not be charged. After payment succeeds, you will see a confirmation screen.'
    : isBid
      ? 'Your points will be committed to this auction bid.'
      : isRaffle
        ? drawingPassed
          ? 'This raffle has ended and is no longer accepting entries.'
          : 'Your points will be spent when you enter. Raffle entries cannot be undone.'
        : 'Your points will be spent when you redeem. Redemptions cannot be undone.';
  const confirmLabel = busy
    ? 'Processing...'
    : isStripe
      ? 'Continue to payment'
      : insufficient
        ? 'Insufficient Points'
        : 'Confirm';

  return (
    <Dialog label="Confirm" onClose={onClose} busy={busy}>
      {isRaffle ? (
        <Badge icon={<TicketIcon aria-hidden="true" />}>Raffle</Badge>
      ) : null}
      <h3 className="rd-dialog-title">{product.title}</h3>
      {isRaffle && !isStripe && !isBid && maxEntries > 1 ? (
        <div className="rd-qty">
          <span>How many entries?</span>
          <span className="rd-qty-row">
            <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1} aria-label="Fewer entries">
              −
            </button>
            <b>{quantity}</b>
            <button
              type="button"
              className="is-add"
              onClick={() => setQuantity((q) => Math.min(maxEntries, q + 1))}
              disabled={quantity >= maxEntries}
              aria-label="More entries"
            >
              +
            </button>
          </span>
        </div>
      ) : null}
      <div className="rd-summary">
        <div>
          <span>{costLabel}</span>
          <strong>{costValue}</strong>
        </div>
        <div>
          <span>{balanceLabel}</span>
          <strong>{balanceValue}</strong>
        </div>
        {!isStripe ? (
          <div>
            <span>Remaining after</span>
            <strong>{remainingValue}</strong>
          </div>
        ) : null}
      </div>
      {requiresAge ? (
        <label className={`rd-age${ageFlash ? ' is-flash' : ''}`}>
          <input type="checkbox" checked={ageConfirmed} onChange={(event) => setAgeConfirmed(event.target.checked)} />
          <span>I confirm that I am at least 18 years old.</span>
        </label>
      ) : null}
      <p className="rd-muted">{note}</p>
      {error ? <p className="rd-error">{error}</p> : null}
      <div className="rd-dialog-actions">
        <button type="button" className="rd-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="rd-btn is-primary"
          onClick={confirm}
          disabled={busy || insufficient || (!isBid && isRaffle && drawingPassed)}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

function ProductActions({ product, user, availablePoints, cardStatus, now, autoEnter, onRefresh }) {
  const [dialog, setDialog] = useState(null);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [bidError, setBidError] = useState('');
  const autoOpened = useRef(false);

  const earlyRelease = isEarlyReleaseUser(user);
  const isLocked = !!product.is_locked || isRewardBeforeStart(product, now);
  const cardLocked = !earlyRelease && cardStatus === 'unlinked';
  const blocked = isLocked || cardLocked;
  const type = product.redemption_type || 'checkout';
  const pointsCost = Number(product.points_cost) || 0;
  const canRedeem = availablePoints >= pointsCost;

  const raffle = product.raffle_info;
  const raffleCompleted = type === 'raffle' && raffle?.status === 'completed';
  const drawingPassed = type === 'raffle' && !!raffle?.drawing_date && now >= new Date(raffle.drawing_date).getTime();
  const userWonRaffle = raffleCompleted && sameId(raffle?.winner, user?.id);
  const participated = (raffle?.user_entries || 0) > 0;
  const canEnterRaffle = type === 'raffle' && canRedeem && !isLocked && !drawingPassed && !raffleCompleted;

  const auction = type === 'auction' ? product.auction_info : null;
  const auctionExpired = !!(auction?.end_date && now >= new Date(auction.end_date).getTime());
  const minimumBid = auction
    ? auction.current_highest_bid
      ? auction.current_highest_bid + (auction.minimum_increment || 0)
      : auction.starting_bid
    : 0;
  const canAffordBid = auction ? availablePoints >= minimumBid : true;
  const isHighestBidder = !!(auction?.user_current_bid && auction.user_current_bid === auction.current_highest_bid);
  const userWonAuction = auction?.status === 'ended' && sameId(auction?.final_winner, user?.id);

  const stripeCents = Number(product.cash_price_cents);
  const isCardOnly = type === 'card';
  const canPayWithStripe =
    Number.isFinite(stripeCents) && stripeCents >= 50 && (type === 'first' || isCardOnly) && !blocked;

  useEffect(() => {
    if (!autoEnter || autoOpened.current) return;
    if (type === 'raffle' && canEnterRaffle && !blocked) {
      autoOpened.current = true;
      setDialog({ mode: 'points' });
    }
  }, [autoEnter, type, canEnterRaffle, blocked]);

  const submitBid = (event) => {
    event.preventDefault();
    const amount = parseFloat(bidAmount);
    if (!bidAmount || Number.isNaN(amount) || amount <= 0) {
      setBidError('Please enter a valid bid amount.');
      return;
    }
    if (amount < minimumBid) {
      setBidError(`Bid must be at least ${formatNumber(minimumBid)} points.`);
      return;
    }
    if (amount > availablePoints) {
      setBidError(`You have ${formatNumber(availablePoints)} points available.`);
      return;
    }
    setBidError('');
    setDialog({ mode: 'bid', bidAmount: amount });
  };

  const closeDialog = () => {
    if (dialog?.mode === 'bid') {
      setBidOpen(false);
      setBidAmount('');
    }
    setDialog(null);
  };

  let body = null;
  if (auction) {
    if (!userWonAuction) {
      const disabled = auctionExpired || !canAffordBid || isHighestBidder || blocked;
      body = bidOpen && !blocked ? (
        <form className="rd-bid" onSubmit={submitBid}>
          <div className="rd-bid-meta">
            <span>Current Bid: {formatNumber(auction.current_highest_bid || auction.starting_bid)} pts</span>
            <span>Minimum Bid: {formatNumber(minimumBid)} pts</span>
          </div>
          <input
            className="rd-input"
            inputMode="numeric"
            value={bidAmount}
            onChange={(event) => setBidAmount(event.target.value.replace(/[^\d.]/g, ''))}
            placeholder="Enter bid amount"
            autoFocus
          />
          {bidError ? <p className="rd-error">{bidError}</p> : null}
          <div className="rd-bid-actions">
            <button type="button" className="rd-btn" onClick={() => { setBidOpen(false); setBidAmount(''); setBidError(''); }}>
              Cancel
            </button>
            <button type="submit" className="rd-btn is-primary">Submit Bid</button>
          </div>
        </form>
      ) : (
        <button type="button" className="rd-cta" disabled={disabled} onClick={() => setBidOpen(true)}>
          {isLocked
            ? 'Locked'
            : auctionExpired
              ? 'Auction Ended'
              : canAffordBid
                ? 'Place Bid'
                : `Minimum Bid: ${formatNumber(minimumBid)} pts`}
        </button>
      );
    }
  } else if (type === 'raffle') {
    let state = null;
    if (userWonRaffle) {
      state = (
        <div className="rd-state is-win">
          <strong>Congratulations!</strong>
          <span>You won this raffle!</span>
          <Link to="/support/" className="rd-btn is-primary">Contact Support</Link>
        </div>
      );
    } else if (drawingPassed && !raffleCompleted) {
      state = (
        <div className="rd-state">
          <strong>Raffle Complete</strong>
          <span>This raffle has ended. Check out our other active raffles for a chance to win!</span>
        </div>
      );
    } else if (raffleCompleted && participated) {
      state = (
        <div className="rd-state">
          <strong>Thanks for participating!</strong>
          <span>You didn&apos;t win this time, but keep trying! Check out other raffles for more chances to win.</span>
        </div>
      );
    } else if (raffleCompleted) {
      state = (
        <div className="rd-state">
          <strong>Raffle Complete</strong>
          <span>This raffle has ended. Check out our other active raffles for a chance to win!</span>
        </div>
      );
    }
    body = (
      <>
        {isLocked && raffle?.start_date ? <p className="rd-opens">Opens {formatOpensDate(raffle.start_date)}</p> : null}
        {state || (
          <button
            type="button"
            className="rd-cta"
            disabled={!canEnterRaffle || blocked}
            onClick={() => setDialog({ mode: 'points' })}
          >
            {isLocked ? 'Locked' : `Enter raffle (${formatNumber(pointsCost)} pts)`}
          </button>
        )}
      </>
    );
  } else {
    body = (
      <div className="rd-cta-group">
        {!isCardOnly ? (
          <button type="button" className="rd-cta" disabled={!canRedeem || blocked} onClick={() => setDialog({ mode: 'points' })}>
            {isLocked ? 'Locked' : canRedeem ? `Redeem for ${formatNumber(pointsCost)} pts` : 'Redeem'}
          </button>
        ) : null}
        {canPayWithStripe ? (
          <button type="button" className="rd-cta is-dark" onClick={() => setDialog({ mode: 'stripe' })}>
            {`Pay $${(stripeCents / 100).toFixed(2)} with card`}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {cardLocked && cardStatus !== 'unknown' ? <LinkCardBanner /> : null}
      {body}
      {dialog ? (
        <ConfirmDialog
          product={product}
          mode={dialog.mode}
          bidAmount={dialog.bidAmount}
          availablePoints={availablePoints}
          onClose={closeDialog}
          onDone={onRefresh}
        />
      ) : null}
    </>
  );
}

function ProductDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user, setUser } = useShellUser();
  const [product, setProduct] = useState(null);
  const [cardStatus, setCardStatus] = useState('unknown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const [reward, freshUser, cards] = await Promise.all([
        apiService.getReward(id),
        apiService.fetchCurrentUser().catch(() => null),
        apiService.getPaymentCards().catch(() => null),
      ]);
      setProduct(reward);
      setError('');
      if (freshUser) setUser(freshUser);
      setCardStatus(cardLinkStatus(freshUser || apiService.getCurrentUser(), cards));
    } catch (_error) {
      setError("We couldn't load this reward. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id, setUser]);

  useEffect(() => {
    setLoading(true);
    setProduct(null);
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const activeAuction =
    product?.redemption_type === 'auction' &&
    product.auction_info?.status === 'active' &&
    !product.is_locked &&
    !(product.auction_info?.end_date && now >= new Date(product.auction_info.end_date).getTime());

  useEffect(() => {
    if (!activeAuction) return undefined;
    const timer = window.setInterval(() => {
      apiService.getReward(id).then(setProduct).catch(() => {});
    }, 10000);
    return () => window.clearInterval(timer);
  }, [activeAuction, id]);

  if (loading) {
    return (
      <DetailFrame>
        <p className="rd-empty">Loading reward...</p>
      </DetailFrame>
    );
  }
  if (error || !product) {
    return (
      <DetailFrame>
        <p className="rd-empty">{error || 'This reward is no longer available.'}</p>
      </DetailFrame>
    );
  }

  const type = product.redemption_type || '';
  const availablePoints = Number(user?.current_points) || 0;
  const earlyRelease = isEarlyReleaseUser(user);
  const raffle = product.raffle_info;
  const auction = product.auction_info;
  const timeLocked = isRewardBeforeStart(product, now);
  const pastEvent =
    (type === 'raffle' && raffle?.drawing_date && parseDateEndOfDay(raffle.drawing_date) < new Date(now)) ||
    (type === 'auction' && auction?.end_date && parseDateEndOfDay(auction.end_date) < new Date(now));
  const isCompleted = !product.is_active || !!pastEvent;
  const userWonRaffle = type === 'raffle' && raffle?.status === 'completed' && sameId(raffle?.winner, user?.id);
  const userWonAuction = type === 'auction' && auction?.status === 'ended' && sameId(auction?.final_winner, user?.id);
  const auctionExpired = !!(auction?.end_date && now >= new Date(auction.end_date).getTime());
  const showActions =
    product.reward_type !== 'gift_card' &&
    (!isCompleted || type === 'auction' || type === 'raffle') &&
    !userWonAuction &&
    !userWonRaffle;
  const overlayPoints = earlyRelease ? 0 : Number(product.points_cost) || 0;
  const badgeLabel = type === 'raffle' ? 'Raffle' : type === 'auction' ? 'Auction' : formatRewardTypeLabel(product.reward_type);

  const rows = [];
  if (type === 'raffle' && raffle) {
    const entries = raffle.user_entries || 0;
    rows.push({ key: 'entries', Icon: MedalIcon, title: 'Your Entries', body: `${formatNumber(entries)} ${entries === 1 ? 'entry' : 'entries'} in this raffle` });
    if (timeLocked && raffle.start_date) {
      rows.push({ key: 'opens', Icon: DateIcon, title: 'Opens', body: formatLongDate(raffle.start_date) });
    }
    if (raffle.drawing_date) {
      rows.push({ key: 'drawing', Icon: DateIcon, title: 'Drawing Date', body: formatEventDateLabel(raffle.drawing_date) || formatLongDate(raffle.drawing_date) });
    }
  }
  if (type === 'auction' && auction) {
    rows.push({ key: 'bid', Icon: MedalIcon, title: 'Current Bid', body: `${formatNumber(auction.current_highest_bid || 0)} points` });
  }
  if (product.description) {
    rows.push({ key: 'desc', Icon: GiftIcon, title: 'Description', body: product.description });
  }
  if (!userWonRaffle) {
    let how = '';
    if (type === 'raffle') {
      if (product.points_cost) {
        how = `Redeem ${formatNumber(product.points_cost)} points for an entry. Winners are drawn at the end of the raffle and contacted with details.`;
      }
    } else if (type === 'auction') {
      how = 'Place a bid with your points. The highest bid when the auction closes wins.';
    } else if (product.points_cost) {
      how =
        availablePoints >= product.points_cost
          ? `You have enough points to redeem this reward. Redeeming spends ${formatNumber(product.points_cost)} points from your balance.`
          : `Earn ${formatNumber(product.points_cost - availablePoints)} more points to unlock this reward. Once unlocked, you can redeem it for ${formatNumber(product.points_cost)} points.`;
    }
    if (how) {
      rows.push({ key: 'how', Icon: type === 'raffle' ? MedalIcon : UnlockIcon, title: type === 'raffle' ? 'How to Win' : 'How to Unlock', body: how });
    }
  }
  if (product.terms || product.terms_and_conditions) {
    rows.push({ key: 'terms', Icon: ShieldIcon, title: 'Terms', body: product.terms || product.terms_and_conditions });
  }

  return (
    <DetailFrame>
      <div className="rd-grid">
        <Gallery images={productImages(product)} overlay={overlayPoints ? <PointsOverlay points={overlayPoints} /> : null} dimmed={isCompleted} />
        <div className="rd-side">
          <div className={`rd-head${isCompleted ? ' is-dimmed' : ''}`}>
            <Badge icon={type === 'raffle' ? <TicketIcon aria-hidden="true" /> : null}>{badgeLabel}</Badge>
            <h1>{product.title}</h1>
          </div>
          {userWonAuction ? (
            <div className="rd-state is-win">
              <strong>Congratulations, You Won!</strong>
              <span>You won this auction with a bid of {formatNumber(auction?.current_highest_bid || 0)} points</span>
              <span>You will be contacted with more details</span>
            </div>
          ) : null}
          {userWonRaffle ? (
            <div className="rd-state is-win">
              <strong>Congratulations, You Won!</strong>
              <span>You won the raffle drawing for this prize!</span>
              <span>You will be contacted with more details</span>
            </div>
          ) : null}
          {type === 'auction' && auction && !userWonRaffle ? (
            <>
              {!auctionExpired && auction.user_current_bid ? (
                <p className={`rd-bidstatus${isHighestBidder(auction) ? ' is-winning' : ''}`}>
                  {isHighestBidder(auction) ? "You're the highest bidder!" : "You've been outbid"}
                </p>
              ) : null}
              <p className="rd-pill">
                {auctionExpired || auction.status === 'ended' || auction.status === 'completed'
                  ? 'Auction ended'
                  : auction.end_date
                    ? longCountdown(new Date(auction.end_date).getTime(), now)
                    : 'Auction live'}
                {auction.total_bids ? ` · ${formatNumber(auction.total_bids)} bids` : ''}
              </p>
            </>
          ) : null}
          <div className={isCompleted ? 'is-dimmed' : ''}>
            <InfoCard rows={rows} />
          </div>
          {isCompleted ? (
            <div className="rd-state">
              <strong>This Event Has Ended</strong>
              {type === 'raffle' && raffle?.drawing_date ? <span>Drawing was on {formatLongDate(raffle.drawing_date)}</span> : null}
              {type === 'auction' && auction?.end_date ? <span>Auction ended on {formatLongDate(auction.end_date)}</span> : null}
              {auction?.final_winner ? <span>Winner will be notified via email</span> : null}
            </div>
          ) : null}
          {showActions ? (
            <ProductActions
              product={product}
              user={user}
              availablePoints={availablePoints}
              cardStatus={cardStatus}
              now={now}
              autoEnter={params.get('enter') === '1'}
              onRefresh={load}
            />
          ) : null}
        </div>
      </div>
    </DetailFrame>
  );
}

function isHighestBidder(auction) {
  return !!(auction?.user_current_bid && auction.user_current_bid === auction.current_highest_bid);
}

function capitalizeWord(word) {
  const value = String(word || '').toLowerCase();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}

function leaderboardName(row) {
  const full = String(row?.name || row?.full_name || row?.fullName || row?.display_name || row?.displayName || row?.username || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!full) return 'User';
  const parts = full.split(' ').filter(Boolean);
  if (parts.length === 1) return capitalizeWord(parts[0]);
  return `${capitalizeWord(parts[0])} ${capitalizeWord(parts[parts.length - 1]).charAt(0)}.`;
}

function rowUserId(row) {
  if (!row || typeof row !== 'object') return '';
  let raw = row.user_id ?? row.userId ?? row.user ?? row.id;
  if (raw == null) return '';
  if (typeof raw === 'object') return raw.id != null ? String(raw.id) : '';
  return String(raw);
}

function isCurrentUserRow(row, user) {
  if (!row || !user) return false;
  const userId = user.id != null ? String(user.id) : '';
  const rowId = rowUserId(row);
  if (userId && rowId) return userId === rowId;
  return false;
}

function prizeImages(prize) {
  const urls = [];
  const push = (url) => {
    const next = normalizeMediaUrl(url);
    if (next && !urls.includes(next)) urls.push(next);
  };
  if (Array.isArray(prize?.resolved_image_urls)) prize.resolved_image_urls.forEach(push);
  if (Array.isArray(prize?.images)) {
    prize.images
      .slice()
      .sort((a, b) => ((a && a.order) || 0) - ((b && b.order) || 0))
      .forEach((img) => {
        if (typeof img === 'string') push(img);
        else if (img) push(img.image_url || img.imageUrl || img.image_path || img.imagePath);
      });
  }
  push(prize?.cover_image_url || prize?.image_url || prize?.cover_image || prize?.resolved_image_url);
  return urls;
}

function prizeEndMs(prize, type, board) {
  if (type === 'season') {
    return parsePeriodEndTimestamp(prize?.overall_ends_at || prize?.end_date, prize?.end_date, prize?.end_time);
  }
  return parsePeriodEndTimestamp(prize?.week_end_at || board?.week_ends_at, prize?.week_end_date);
}

function RankMark({ rank }) {
  const n = Number(rank);
  if (n >= 1 && n <= 3) {
    const tone = n === 1 ? '#FFB804' : n === 2 ? '#DADADA' : '#A36A25';
    return (
      <span className="rd-rank-medal" style={{ color: tone }}>
        <RankMedalIcon aria-hidden="true" />
      </span>
    );
  }
  return <span className="rd-rank-num">{rank}</span>;
}

function periodStats(board, tab) {
  const stats = tab === 'season' ? board?.overall_user_stats : board?.user_stats;
  if (!stats) return null;
  const points = Number(stats.points);
  return {
    points: Number.isFinite(points) ? points : 0,
    rank: stats.rank != null ? stats.rank : null,
  };
}

function Leaderboard({ board, user, initialTab }) {
  const [tab, setTab] = useState(initialTab);
  const [expanded, setExpanded] = useState(false);
  const hasSeason = Array.isArray(board?.overall_leaderboard) && (board.overall_leaderboard.length || board?.overall_prize);
  const rows = useMemo(
    () => (tab === 'season' ? board?.overall_leaderboard || [] : board?.leaderboard || []),
    [tab, board]
  );
  const mine = useMemo(() => periodStats(board, tab), [board, tab]);

  const entries = useMemo(() => {
    let youIndex = -1;
    const list = (Array.isArray(rows) ? rows : []).slice(0, MAX_ROWS).map((row, index) => {
      const you = isCurrentUserRow(row, user) && youIndex < 0;
      if (you) youIndex = index;
      return {
        row: you && mine ? { ...row, points: mine.points } : row,
        rank: you && mine?.rank != null ? mine.rank : row?.rank != null ? row.rank : index + 1,
        you,
        index,
      };
    });
    if (youIndex < 0 && user && mine) {
      list.push({
        row: { id: user.id, points: mine.points },
        rank: mine.rank != null ? mine.rank : '—',
        you: true,
        index: list.length,
      });
    }
    if (expanded) return list;
    const visible = list.slice(0, COLLAPSED_ROWS);
    const youAt = list.findIndex((entry) => entry.you);
    if (youAt >= COLLAPSED_ROWS) visible.push(list[youAt]);
    return visible;
  }, [rows, user, expanded, mine]);

  const half = Math.ceil(entries.length / 2);
  const columns = [entries.slice(0, half), entries.slice(half)].filter((col) => col.length);

  return (
    <section className="rd-board" aria-label="Leaderboard">
      <div className="rd-board-head">
        <h2>Leaderboard</h2>
        {rows.length > COLLAPSED_ROWS ? (
          <button type="button" onClick={() => setExpanded((open) => !open)}>
            {expanded ? 'Show less' : 'View all'}
          </button>
        ) : null}
      </div>
      {hasSeason ? (
        <div className="rd-seg" role="tablist">
          {[
            ['weekly', 'Week'],
            ['season', 'Season'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? 'is-on' : ''}
              onClick={() => {
                setTab(value);
                setExpanded(false);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {!entries.length ? (
        <p className="rd-empty">No rankings yet.</p>
      ) : (
        <div className="rd-board-cols">
          {columns.map((col, colIndex) => (
            <ul key={colIndex}>
              {col.map(({ row, rank, you, index }) => {
                const avatar = String(row?.avatar_url || row?.avatarUrl || row?.profile_image || '').trim();
                return (
                  <li key={`${rank}-${rowUserId(row) || index}`} className={you ? 'is-you' : ''}>
                    <RankMark rank={rank} />
                    <span className="rd-avatar">
                      {avatar ? <img src={avatar} alt="" /> : <PersonIcon aria-hidden="true" />}
                    </span>
                    <span className="rd-board-name">{you ? 'You' : leaderboardName(row)}</span>
                    <span className="rd-board-pts">{formatNumber(row?.points)} pts</span>
                  </li>
                );
              })}
            </ul>
          ))}
        </div>
      )}
    </section>
  );
}

function PrizeDetail() {
  const { type: rawType } = useParams();
  const type = rawType === 'season' ? 'season' : 'weekly';
  const { user, setUser } = useShellUser();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const refreshedAtEnd = useRef(false);

  const load = useCallback(async () => {
    const [res, freshUser] = await Promise.all([
      apiService.getLeaderboard().catch(() => null),
      apiService.fetchCurrentUser().catch(() => null),
    ]);
    if (freshUser) setUser(freshUser);
    if (res?.success !== false) setBoard(res);
    setLoading(false);
  }, [setUser]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const prize = type === 'season' ? board?.overall_prize : board?.weekly_prize;
  const endMs = prize ? prizeEndMs(prize, type, board) : null;
  const winnerName = String(prize?.winner_name || '').trim();

  useEffect(() => {
    refreshedAtEnd.current = false;
  }, [prize?.id, endMs]);

  useEffect(() => {
    if (!endMs) return undefined;
    const trigger = () => {
      if (refreshedAtEnd.current) return;
      refreshedAtEnd.current = true;
      load();
    };
    const wait = endMs - Date.now();
    if (wait <= 0) {
      trigger();
      return undefined;
    }
    const timeout = window.setTimeout(trigger, wait + 500);
    return () => window.clearTimeout(timeout);
  }, [endMs, load]);

  useEffect(() => {
    if (!endMs || Date.now() < endMs || winnerName) return undefined;
    const interval = window.setInterval(load, 15000);
    return () => window.clearInterval(interval);
  }, [endMs, winnerName, load, now]);

  if (loading) {
    return (
      <DetailFrame>
        <p className="rd-empty">Loading prize...</p>
      </DetailFrame>
    );
  }
  if (!prize) {
    return (
      <DetailFrame>
        <p className="rd-empty">This prize is no longer available.</p>
      </DetailFrame>
    );
  }

  const title = prize.title || prize.cover_title || prize.resolved_reward_title || prize.name || 'Reward';
  const description = prize.description || prize.cover_text || '';
  const location = String(prize.resolved_location || prize.location || prize.venue || prize.place || '').trim();
  const eventDate = formatEventDateLabel(
    String(prize.event_date || prize.eventDate || prize.event_datetime || prize.eventDatetime || prize.date || '').trim()
  );
  const rows = [];
  if (eventDate) rows.push({ key: 'date', Icon: DateIcon, title: 'Date', body: eventDate });
  if (location) rows.push({ key: 'location', Icon: LocationIcon, title: 'Location', body: location });
  rows.push({ key: 'how', Icon: MedalIcon, title: 'How to Win', body: prize.how_to_win || DEFAULT_HOW_TO_WIN });
  if (prize.terms) rows.push({ key: 'terms', Icon: ShieldIcon, title: 'Terms', body: prize.terms });
  const countdown = compactCountdown(endMs, now, 'Ended');

  return (
    <DetailFrame>
      <div className="rd-grid">
        <Gallery images={prizeImages(prize)} overlay={countdown ? <b>{countdown}</b> : null} />
        <div className="rd-side">
          <div className="rd-head">
            <Badge>{type === 'season' ? 'Season Prize' : 'Weekly Prize'}</Badge>
            <h1>{title}</h1>
            {description ? <p>{description}</p> : null}
          </div>
          {winnerName ? (
            <div className="rd-state is-win">
              <strong>{type === 'season' ? 'Overall Winner' : 'Weekly Winner'}</strong>
              <span>{winnerName}</span>
            </div>
          ) : null}
          <InfoCard rows={rows} />
        </div>
      </div>
      <Leaderboard board={board} user={user} initialTab={type} />
    </DetailFrame>
  );
}

function normalizeMilestone(raw) {
  if (!raw) return null;
  const cost = Number(raw.pointsCost != null ? raw.pointsCost : raw.points_cost) || 0;
  if (cost <= 0) return null;
  return {
    id: raw.id != null ? String(raw.id) : '',
    title: raw.title || 'Reward',
    description: raw.description || '',
    whatsIncluded: raw.whatsIncluded || raw.whats_included || '',
    location: raw.location || '',
    pointsCost: cost,
    images: Array.isArray(raw.images) ? raw.images.filter(Boolean) : [],
    imageUrl: raw.imageUrl || raw.image_url || '',
    confirmationImageUrl: raw.confirmationImageUrl || raw.confirmation_image_url || '',
    unlocked: !!raw.unlocked,
    redeemed: !!raw.redeemed,
  };
}

function milestoneImages(milestone) {
  const urls = [];
  const push = (url) => {
    const next = normalizeMediaUrl(url);
    if (next && !urls.includes(next)) urls.push(next);
  };
  (milestone?.images || []).forEach(push);
  push(milestone?.imageUrl);
  push(milestone?.confirmationImageUrl);
  return urls;
}

function phoneDigits(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

function formatPhone(value) {
  const digits = phoneDigits(value);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function FirstRedeemDialog({ milestone, user, onClose, onDone }) {
  const accountEmail = String(user?.email || '').trim();
  const accountPhone = formatPhone(user?.phone_number || '');
  const [method, setMethod] = useState('sms');
  const [value, setValue] = useState(accountPhone);
  const [saved, setSaved] = useState({ sms: accountPhone, email: '' });
  const [fieldError, setFieldError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const switchMethod = (next) => {
    if (next === method) return;
    const nextSaved = { ...saved, [method]: value.trim() };
    setSaved(nextSaved);
    setMethod(next);
    setFieldError('');
    setValue(nextSaved[next] || (next === 'email' ? accountEmail : accountPhone) || '');
  };

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setFieldError(method === 'sms' ? 'Enter a phone number.' : 'Enter an email address.');
      return;
    }
    if (method === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFieldError('Enter a valid email address, like you@school.edu.');
      return;
    }
    if (method === 'sms') {
      const digits = phoneDigits(trimmed);
      if (digits.length !== 10 || /^[01]/.test(digits)) {
        setFieldError('Enter a valid 10-digit US phone number.');
        return;
      }
    }
    setBusy(true);
    setFieldError('');
    try {
      await apiService.redeemFirstReward(milestone.id, { contactMethod: method, contactValue: trimmed });
      setDone(true);
      onDone();
    } catch (err) {
      setFieldError(errorText(err, "That didn't go through. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Dialog label="Reward redeemed" onClose={onClose}>
        <div className="rd-thanks">
          <span className="rd-thanks-mark" aria-hidden="true">✓</span>
          <h3>Reward redeemed</h3>
          <p>We&apos;ll reach out to arrange {milestone.title}.</p>
          <div className="rd-dialog-actions">
            <button type="button" className="rd-btn is-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog label="Redeem reward" onClose={onClose} busy={busy}>
      <form className="rd-first" onSubmit={submit}>
        <span className="rd-eyebrow">Reward unlocked!</span>
        <h3 className="rd-dialog-title">{milestone.title}</h3>
        <p className="rd-muted">
          {`You've reached ${formatNumber(milestone.pointsCost)} points. ${milestone.title} is ready to redeem.`}
        </p>
        <span className="rd-label">How should we reach you to arrange it?</span>
        <div className="rd-seg is-full">
          <button type="button" className={method === 'sms' ? 'is-on' : ''} onClick={() => switchMethod('sms')}>
            Text me
          </button>
          <button type="button" className={method === 'email' ? 'is-on' : ''} onClick={() => switchMethod('email')}>
            Email me
          </button>
        </div>
        <input
          className="rd-input"
          value={value}
          onChange={(event) => {
            setValue(method === 'sms' ? formatPhone(event.target.value) : event.target.value);
            setFieldError('');
          }}
          placeholder={method === 'email' ? 'you@school.edu' : '(555) 555-5555'}
          type={method === 'email' ? 'email' : 'tel'}
          autoComplete={method === 'email' ? 'email' : 'tel'}
          maxLength={method === 'sms' ? 14 : undefined}
        />
        {fieldError ? <p className="rd-error">{fieldError}</p> : null}
        <div className="rd-dialog-actions">
          <button type="button" className="rd-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="rd-btn is-primary" disabled={busy}>
            {busy ? 'Redeeming...' : 'Redeem'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function FirstRewardDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useShellUser();
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redeemOpen, setRedeemOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [payload, freshUser] = await Promise.all([
        apiService.getFirstReward(id),
        apiService.fetchCurrentUser().catch(() => null),
      ]);
      if (freshUser) setUser(freshUser);
      const next = normalizeMilestone(payload?.milestone);
      setMilestone(next);
      setError(next ? '' : 'This reward is no longer available.');
    } catch (_error) {
      setMilestone(null);
      setError("We couldn't load this reward. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id, setUser]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const claimable = !!(milestone?.unlocked && !milestone?.redeemed);

  useEffect(() => {
    if (params.get('redeem') !== '1' || !milestone) return;
    if (claimable) setRedeemOpen(true);
    setParams({}, { replace: true });
  }, [params, milestone, claimable, setParams]);

  if (loading) {
    return (
      <DetailFrame>
        <p className="rd-empty">Loading reward...</p>
      </DetailFrame>
    );
  }
  if (error || !milestone) {
    return (
      <DetailFrame>
        <p className="rd-empty">{error || 'This reward is no longer available.'}</p>
      </DetailFrame>
    );
  }

  const unlockCopy = milestone.redeemed
    ? "You've redeemed this reward. Check the contact details you gave us for instructions."
    : milestone.unlocked
      ? "You've earned enough points. Click Redeem to claim it and tell us how to reach you."
      : `Earn ${formatNumber(milestone.pointsCost)} points to unlock this experience. Once unlocked, you'll receive instructions for confirming your attendance.`;
  const rows = [];
  if (milestone.whatsIncluded) rows.push({ key: 'included', Icon: GiftIcon, title: "What's Included", body: milestone.whatsIncluded });
  if (milestone.location) rows.push({ key: 'location', Icon: LocationIcon, title: 'Location', body: milestone.location });
  rows.push({ key: 'unlock', Icon: UnlockIcon, title: 'How to Unlock', body: unlockCopy });
  const label = milestone.redeemed ? 'Redeemed' : milestone.unlocked ? 'Redeem' : `Locked — ${formatNumber(milestone.pointsCost)} pts`;

  return (
    <DetailFrame>
      <div className="rd-grid">
        <Gallery images={milestoneImages(milestone)} overlay={<PointsOverlay points={milestone.pointsCost} />} />
        <div className="rd-side">
          <div className="rd-head">
            <Badge>Reward</Badge>
            <h1>{milestone.title}</h1>
            {milestone.description ? <p>{milestone.description}</p> : null}
          </div>
          <InfoCard rows={rows} />
          <button
            type="button"
            className={`rd-cta${milestone.redeemed ? ' is-done' : ''}`}
            disabled={!claimable}
            onClick={() => setRedeemOpen(true)}
          >
            {label}
          </button>
        </div>
      </div>
      {redeemOpen ? (
        <FirstRedeemDialog
          milestone={milestone}
          user={user}
          onClose={() => {
            setRedeemOpen(false);
            if (milestone.redeemed) navigate('/rewards');
          }}
          onDone={load}
        />
      ) : null}
    </DetailFrame>
  );
}

function gate(Page) {
  return function RewardDetailGate() {
    if (!apiService.isAuthenticated()) return <TempStart />;
    return (
      <AppShell title="Rewards">
        <Page />
      </AppShell>
    );
  };
}

export const RewardItemGate = gate(ProductDetail);
export const RewardPrizeGate = gate(PrizeDetail);
export const FirstRewardGate = gate(FirstRewardDetail);
